import React, { useMemo, useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import SearchInput from '../../shared/components/SearchInput';
import EmptyState from '../../shared/components/EmptyState';
import Skeleton from '../../shared/components/Skeleton';
import Pagination from '../../shared/components/Pagination';
import Table from '../../shared/components/Table';
import StatusPill from '../../shared/components/StatusPill';
import { exportCsv } from '../../shared/importExport';
import { date } from '../../shared/formatters';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';

const STATUS_FILTERS = ['all', 'new', 'contacted', 'converted', 'declined'];
const TIER_LABELS = {
  starter: 'Starter',
  professional: 'Professional',
  business: 'Business',
  enterprise: 'Enterprise',
};

// Admin lead inbox for the public /sign-up marketing page (migration 025).
// Rows are created exclusively by portal-api's POST /api/signups; this
// screen only works the status pipeline: new → contacted → converted.
export default function SignupsList() {
  const { data, ready, update } = useData();
  const { notify } = useToast();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const perPage = 20;

  const signups = data.signups || [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return signups.filter((s) => {
      if (status !== 'all' && s.status !== status) return false;
      if (!q) return true;
      return (
        s.agency_name.toLowerCase().includes(q) ||
        s.contact_name.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        (s.notes || '').toLowerCase().includes(q)
      );
    });
  }, [signups, query, status]);

  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const setStatusFor = async (row, next) => {
    if (row.status === next) return;
    try {
      await update('signups', row.id, { status: next });
      notify(`Marked ${row.agency_name} as ${next}`);
    } catch (err) {
      notify(err.message || 'Could not update the lead', 'error');
    }
  };

  const exportLeads = () => {
    exportCsv('signups', filtered, [
      { label: 'Agency', value: (s) => s.agency_name },
      { label: 'Contact', value: (s) => s.contact_name },
      { label: 'Email', value: (s) => s.email },
      { label: 'Phone', value: (s) => s.phone || '' },
      { label: 'Engineers', value: (s) => s.engineers || '' },
      { label: 'Plan', value: (s) => TIER_LABELS[s.tier] || s.tier },
      { label: 'Status', value: (s) => s.status },
      { label: 'Received', value: (s) => s.created || '' },
      { label: 'Notes', value: (s) => s.notes || '' },
    ]);
  };

  if (!ready) {
    return <div className="standalone-page"><Skeleton rows={6} /></div>;
  }

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Growth</span>
          <h1>Sign-up requests</h1>
        </div>
        <div className="header-actions">
          <button className="secondary-button" onClick={exportLeads} disabled={!filtered.length}>
            <SafeIcon icon={Icons.Download} />
            Export CSV
          </button>
        </div>
      </header>

      <p className="signups-intro">
        Leads from the public <a href="/#/sign-up" target="_blank" rel="noreferrer">/sign-up page</a>.
        Every submission is stored, rate-limited and alerted via Telegram/email — work them through
        the pipeline below.
      </p>

      <div className="toolbar">
        <SearchInput value={query} onChange={(v) => { setQuery(v); setPage(1); }} placeholder="Search agency, contact, email…" />
        <div className="filter-chips" role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`chip${status === opt ? ' chip-active' : ''}`}
              onClick={() => { setStatus(opt); setPage(1); }}
            >
              {opt === 'all' ? 'All' : opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {!filtered.length ? (
        <EmptyState
          title={signups.length ? 'No matching sign-ups' : 'No sign-up requests yet'}
          message={signups.length
            ? 'Try clearing the search or filter.'
            : 'Requests submitted on the public /sign-up page will appear here, with a Telegram/email alert for each.'}
        />
      ) : (
        <>
          <Table
            columns={[
              {
                key: 'agency_name',
                label: 'Agency',
                render: (row) => (
                  <button
                    type="button"
                    className="signups-expand"
                    onClick={(e) => { e.stopPropagation(); setExpanded(expanded === row.id ? null : row.id); }}
                    aria-expanded={expanded === row.id}
                  >
                    <span>
                      <strong>{row.agency_name}</strong>
                      <span className="signups-sub">{row.contact_name} · {row.email}</span>
                    </span>
                    <SafeIcon icon={expanded === row.id ? Icons.ChevronDown : Icons.ChevronRight} />
                  </button>
                ),
              },
              { key: 'tier', label: 'Plan', render: (row) => TIER_LABELS[row.tier] || row.tier },
              {
                key: 'engineers',
                label: 'Engineers',
                render: (row) => row.engineers || '—',
              },
              {
                key: 'status',
                label: 'Status',
                render: (row) => <StatusPill status={row.status === 'new' ? 'draft' : row.status} />,
              },
              { key: 'created', label: 'Received', render: (row) => date(row.created) },
              {
                key: 'actions',
                label: '',
                render: (row) => (
                  <div className="signups-actions">
                    {row.status === 'new' && (
                      <button className="secondary-button" onClick={(e) => { e.stopPropagation(); setStatusFor(row, 'contacted'); }}>Mark contacted</button>
                    )}
                    {(row.status === 'new' || row.status === 'contacted') && (
                      <button className="primary-button" onClick={(e) => { e.stopPropagation(); setStatusFor(row, 'converted'); }}>Convert</button>
                    )}
                    {row.status !== 'declined' && row.status !== 'converted' && (
                      <button className="secondary-button" onClick={(e) => { e.stopPropagation(); setStatusFor(row, 'declined'); }}>Decline</button>
                    )}
                  </div>
                ),
              },
            ]}
            rows={paged}
            onRowClick={(row) => setExpanded(expanded === row.id ? null : row.id)}
          />
          {paged.map((row) => (
            expanded === row.id && (
              <div className="signups-detail" key={row.id}>
                <div>
                  <h4>Contact</h4>
                  <p>{row.contact_name}</p>
                  <p><a href={`mailto:${row.email}`}>{row.email}</a></p>
                  {row.phone && <p><a href={`tel:${row.phone}`}>{row.phone}</a></p>}
                </div>
                <div>
                  <h4>Requirement</h4>
                  <p>Plan: {TIER_LABELS[row.tier] || row.tier}</p>
                  {row.engineers && <p>Engineers: {row.engineers}</p>}
                  <p>Received: {date(row.created)}</p>
                </div>
                {row.notes ? (
                  <div>
                    <h4>Notes</h4>
                    <p>{row.notes}</p>
                  </div>
                ) : null}
              </div>
            )
          ))}
          <Pagination
            page={page}
            pages={Math.ceil(filtered.length / perPage)}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
}
